import {
  Http400Code,
  Http401And403Code,
  Http409Code,
  Http500Code,
  HttpCodeModel,
} from '@/api/model/HttpErrorCodes.model.ts';
import { QueryClientMetaModel } from '@/config/QueryClientMeta.model';
import { RoutesEnum } from '@/routes/Routes.enum.ts';
import { isAxiosError } from '@/utils/isAxiosError.guard.ts';
import { QueryParams } from '@/utils/QueryParam.enum.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { TFunction } from 'i18next';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { NavigateFunction, useNavigate } from 'react-router-dom';

export const useGetQueryClient = (): QueryClient => {
  const { t } = useTranslationWithPrefix('utils.network');
  const { t: tErrors } = useTranslationWithPrefix('utils.errors');
  const navigate = useNavigate();

  // Refs keep latest closures without invalidating the QueryClient on every render.
  // Without this, `t`/`tErrors`/`navigate` reference changes would recreate the
  // QueryClient — which wipes the cache, refetches every query, and on a 401 page
  // causes an endless loop of `/api/users/me` requests.
  const tRef = useRef(t);
  const tErrorsRef = useRef(tErrors);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    tRef.current = t;
    tErrorsRef.current = tErrors;
    navigateRef.current = navigate;
  });

  const queryClientRef = useRef<QueryClient | null>(null);
  if (queryClientRef.current === null) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (handle401And403(error, tRef.current, navigateRef.current)) {
            return;
          }
          if (handle409(error, tRef.current, navigateRef.current)) {
            return;
          }
          if (handle500(error, tRef.current, navigateRef.current)) {
            return;
          }
          if (
            !mutation?.options?.meta?.disable400 &&
            handle400(error, tRef.current, navigateRef.current)
          ) {
            return;
          }
          if (!mutation?.options?.meta?.disableErrorAlert) {
            toast.error(tErrorsRef.current('unexpectedError'));
          }
          throw error;
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (!(mutation?.options?.meta as Partial<QueryClientMetaModel>)?.disableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown, query) => {
          if (handle401And403(error, tRef.current, navigateRef.current)) {
            return;
          }
          if (handle409(error, tRef.current, navigateRef.current)) {
            return;
          }
          if (handle500(error, tRef.current, navigateRef.current)) {
            return;
          }
          if (
            !query?.options?.meta?.disable400 &&
            handle400(error, tRef.current, navigateRef.current)
          ) {
            return;
          }
          toast.error(tErrorsRef.current('unexpectedError'));
          throw error;
        },
        onSuccess: (_data, query) => {
          if ((query?.options?.meta as Partial<QueryClientMetaModel>)?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
  }

  return queryClientRef.current;
};

const handleOperationSuccess = () => {};

const ottLoginCustomValidation = (error: AxiosError, t: TFunction) => {
  if (!error.config?.url?.includes('login/ott')) {
    return false;
  }
  toast.error(t('loginError'));
  return true;
};

// The backend's answer to an admin outside the allowed networks; the session itself is fine
const IP_NOT_ALLOWED = '403_ip';

export const ottLoginCustomValidationWithRedirect = (
  error: AxiosError,
  t: TFunction,
  navigate: NavigateFunction,
) => {
  const { businessStatus, clientIp } = (error.response?.data ?? {}) as {
    businessStatus?: string;
    clientIp?: string;
  };
  if (error.response?.status === HttpStatusCode.Forbidden && businessStatus === IP_NOT_ALLOWED) {
    // one message for all the requests of the panel, shown long enough to be read
    toast.error(t(IP_NOT_ALLOWED, { ip: clientIp ?? '?' }), {
      duration: 15_000,
      id: IP_NOT_ALLOWED,
    });
    return true;
  }
  if (error.config?.url?.includes('login/ott')) {
    setTimeout(() => {
      navigate(RoutesEnum.INACTIVE);
    }, 0);
    return true;
  }
  if (
    location.pathname === RoutesEnum.ROOT ||
    location.pathname.includes('/login') ||
    location.pathname === RoutesEnum.ADMIN_LOGIN ||
    location.pathname === RoutesEnum.CONFLICT_409 ||
    location.pathname === RoutesEnum.ERROR ||
    location.pathname === RoutesEnum.INACTIVE ||
    location.pathname === RoutesEnum.SERVICE_UNAVAILABLE
  ) {
    return true;
  }
  toast.error(t('401'));

  setTimeout(() => {
    location.pathname = RoutesEnum.ROOT;
  }, 3000);

  return true;
};

const handle400 = (error: unknown, t: TFunction, navigate: NavigateFunction): boolean => {
  return handleError({
    customValidation: ottLoginCustomValidation,
    defaultRedirectRoute: RoutesEnum.ROOT,
    doNotRedirect: true,
    error,
    errorCodes: Http400Code,
    messageFromError: true,
    navigate,
    statuses: [HttpStatusCode.BadRequest],
    t,
  });
};

const handle401And403 = (error: unknown, t: TFunction, navigate: NavigateFunction): boolean => {
  console.debug('handle401');
  return handleError({
    customValidation: (...params) => ottLoginCustomValidationWithRedirect(...params, navigate),
    defaultRedirectRoute: RoutesEnum.ROOT,
    doNotRedirect: true,
    error,
    errorCodes: Http401And403Code,
    messageFromError: true,
    navigate,
    statuses: [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden],
    t,
  });
};

const handle409 = (error: unknown, t: TFunction, navigate: NavigateFunction): boolean => {
  return handleError({
    defaultRedirectRoute: RoutesEnum.CONFLICT_409,
    error,
    errorCodes: Http409Code,
    navigate,
    statuses: [HttpStatusCode.Conflict],
    t,
    timeout: 0,
  });
};

const handle500 = (error: unknown, t: TFunction, navigate: NavigateFunction): boolean => {
  return handleError({
    defaultRedirectRoute: RoutesEnum.ERROR,
    error,
    errorCodes: Http500Code,
    navigate,
    statuses: [HttpStatusCode.InternalServerError],
    t,
  });
};

type HandleErrorParams = {
  customValidation?: (error: AxiosError, t: TFunction) => boolean;
  defaultRedirectRoute: RoutesEnum;
  doNotRedirect?: boolean;
  error: unknown;
  errorCodes: HttpCodeModel;
  messageFromError?: boolean;
  navigate: NavigateFunction;
  statuses: HttpStatusCode[];
  t: TFunction;
  timeout?: number;
};

const handleError = (params: HandleErrorParams): boolean => {
  const {
    customValidation,
    defaultRedirectRoute,
    doNotRedirect,
    error,
    errorCodes,
    messageFromError,
    navigate,
    statuses,
    t,
    timeout,
  } = params;
  if (
    !isAxiosError(error) ||
    !error.response?.status ||
    !statuses.includes(error.response.status)
  ) {
    return false;
  }

  let redirectRouteInner: RoutesEnum = defaultRedirectRoute;
  const { businessStatus, message } = error.response.data as {
    businessStatus?: string;
    message?: string;
  };
  if (customValidation?.(error, t)) {
    // do nothing
  } else if (businessStatus && Object.keys(errorCodes).some(it => it === businessStatus)) {
    if (messageFromError) {
      toast.error(message ?? '');
    } else {
      toast.error(t(businessStatus));
    }
    if (!doNotRedirect) {
      if (errorCodes[businessStatus]) {
        redirectRouteInner =
          errorCodes[businessStatus] + `?${QueryParams.ERROR_CODE}=${businessStatus}`;
      }
    }
  } else {
    toast.error(t(String(statuses)));
  }

  if (!doNotRedirect) {
    setTimeout(() => {
      navigate(redirectRouteInner);
    }, timeout ?? 50);
  }
  return true;
};
